import { Box, Divider, Drawer, IconButton, Paper, Typography, useTheme } from "@material-ui/core";
import { Clear } from "@material-ui/icons";
import CaseEventLog from "apps/platform-client/client/src/components/icons/CaseEventLog";
import { AgreementUserRole } from "libs/utils/src/test-payloads/graphql";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ChatsIcon from "../../../../components/icons/Chats";
import ScenariosIcon from "../../../../components/icons/Scenarios";
import SimilarCasesIcon from "../../../../components/icons/SimilarCases";
import MappedComponent from "../../../../components/mapped-components/MappedComponent";
import useUrlQuery from "../../../../utils/use-url-query";
import { GetCaseByIdQuery } from "../../types/queries.generated";
import useStyles from "../../use-styles";
import { CaseEventLogs } from "./case-event-logs/CaseEventLogs";
import { Chats } from "./Chats";
import { Scenarios } from "./Scenarios";
import { SimilarCases } from "./SimilarCases";

interface IRightPermanentDrawerProps {
	caseId: string;
	agreement: NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>;
	canUserViewFinancials: boolean;
	selectedView: View | undefined;
	agreementUserRole: AgreementUserRole | null;
	handleCloseDrawer: () => void;
	handleToggleOpen: (view: View) => void;
}

type View = "scenarios" | "similar-cases" | "chats" | "case-event-logs";

const RightPermanentDrawer = (props: IRightPermanentDrawerProps) => {
	const { sideDrawer, openDrawer, closedDrawer, iconBar, activeIconContainer, selectedViewName } = useStyles();
	const { palette } = useTheme();
	const { t } = useTranslation();
	const url = useUrlQuery();
	const messageUrlQueryRefundId = url.get("thread");
	const { selectedView, handleCloseDrawer, handleToggleOpen } = props;
	const isLimitedAccessUser = props.agreementUserRole === AgreementUserRole.LimitedAccessUser;

	useEffect(() => {
		if (!messageUrlQueryRefundId || messageUrlQueryRefundId !== props.caseId) return;
		handleToggleOpen("chats");
	}, [messageUrlQueryRefundId]);

	return (
		<Drawer
			classes={{
				paper: `${sideDrawer} ${selectedView ? openDrawer : closedDrawer}`,
			}}
			open={true}
			BackdropProps={{ invisible: true }}
			onClose={handleCloseDrawer}
			anchor="right"
			variant="permanent"
			style={{ overflow: "hidden" }}
		>
			<Box display="flex" alignItems="flex-start" justifyContent="space-between">
				<Paper className={iconBar}>
					<Box>
						{!props.canUserViewFinancials ? (
							<MappedComponent component="Tooltip" props={{ title: t("limited_permissions") as string }}>
								<Box>
									<IconButton
										disabled
										onClick={() => handleToggleOpen("scenarios")}
										style={{ height: "45px", width: "45px", marginLeft: "2px" }}
									>
										<ScenariosIcon color="lightgray" />
									</IconButton>
								</Box>
							</MappedComponent>
						) : (
							<IconButton
								onClick={() => handleToggleOpen("scenarios")}
								className={selectedView === "scenarios" ? activeIconContainer : undefined}
								style={{ height: "45px", width: "45px", marginLeft: "2px" }}
							>
								<ScenariosIcon />
							</IconButton>
						)}
						{isLimitedAccessUser ? (
							<MappedComponent component="Tooltip" props={{ title: t("similar-cases-disabled") as string }}>
								<Box>
									<IconButton
										disabled
										onClick={() => handleToggleOpen("similar-cases")}
										style={{ height: "45px", width: "45px", marginLeft: "2px" }}
									>
										<SimilarCasesIcon color="lightgray" />
									</IconButton>
								</Box>
							</MappedComponent>
						) : (
							<IconButton
								onClick={() => handleToggleOpen("similar-cases")}
								className={selectedView === "similar-cases" ? activeIconContainer : undefined}
								style={{ height: "45px", width: "45px", marginLeft: "2px" }}
							>
								<SimilarCasesIcon />
							</IconButton>
						)}
						<IconButton
							onClick={() => handleToggleOpen("case-event-logs")}
							className={selectedView === "case-event-logs" ? activeIconContainer : undefined}
							style={{ height: "45px", width: "45px", marginLeft: "2px" }}
						>
							<CaseEventLog />
						</IconButton>
						<IconButton
							onClick={() => handleToggleOpen("chats")}
							className={selectedView === "chats" ? activeIconContainer : undefined}
							style={{ height: "45px", width: "45px", marginLeft: "2px" }}
						>
							<ChatsIcon />
						</IconButton>
					</Box>
				</Paper>
				<Box display="flex" flexDirection="column" width="100%">
					{!!selectedView && (
						<Box padding={1} paddingLeft={2.6} width="100%" display="flex" alignItems="center" justifyContent="space-between">
							<Typography color="textSecondary" variant="h6" className={selectedViewName}>
								{t(selectedView).toUpperCase()}
							</Typography>
							<IconButton onClick={handleCloseDrawer}>
								<Clear />
							</IconButton>
						</Box>
					)}
					<Divider color={palette.tableDivider.main} />
					{selectedView === "scenarios" && (
						<Box display="flex" flexDirection="column" width="100%">
							<Scenarios agreement={props.agreement} />
						</Box>
					)}
					{selectedView === "similar-cases" && (
						<Box display="flex" flexDirection="column" width="100%">
							<SimilarCases caseId={props.caseId} />
						</Box>
					)}
					{selectedView === "case-event-logs" && (
						<Box pl={2} padding={1} pt={2} display="flex" flexDirection="column" width="100%">
							<CaseEventLogs caseId={props.caseId} currencyCode={props.agreement.currency?.Code} />
						</Box>
					)}
					{selectedView === "chats" && (
						<Box display="flex" flexDirection="column" width="100%">
							<Chats caseId={props.caseId} />
						</Box>
					)}
				</Box>
			</Box>
		</Drawer>
	);
};

export default RightPermanentDrawer;
